import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./globals.css";
import { I18nextProvider } from "react-i18next";
import i18n from "@/lib/i18n";
import React from "react";
import { AgentIdProvider } from "@/context/AgentIdContext";
import { ErrorDialogProvider } from "@/context/ErrorDialogContext";
import { fetchBrokerByToken } from "@/api/simpego";
import { localizeFlatpickr } from "@/utils/flatpickrLocale";


import * as Sentry from "@sentry/react";

if ((window.__env__.sentryEnv !== "development")) {
  Sentry.init({
    dsn: window.__env__.sentryDsn,
    environment: window.__env__.sentryEnv,
    release: window.__env__.sentryRelease,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
  });
}



declare global {
  interface Window {
    __simpego_widget__?: { locale?: string };
  }
}

const rootElement = document.getElementById("root-simpego-funnel")!;

const normalizeAgentId = (raw: string | null) => {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const agentWithoutPrefix = trimmed.replace(/^va/i, "");
  const digitsOnly = agentWithoutPrefix.replace(/\D/g, "");
  if (!digitsOnly) return null;

  const padded = digitsOnly.padStart(10, "0");
  return `VA${padded}`;
};

const Root = () => {
  const [isI18nReady, setIsI18nReady] = React.useState(false);
  const [initialAgentProfile, setInitialAgentProfile] = React.useState<{ agentId: string | null; agentName: string | null; agentToken: string | null; agentEmails: string[]; isDirectAgent: boolean } | null>(null);

  React.useEffect(() => {
    let partnerName: string | null = null;

    const getParamsFromHashQuery = (hash: string) => {
      const params: { [key: string]: string } = {};
      const hashQuery = hash.includes('?') ? hash.split('?')[1] : '';
      if (hashQuery) {
        new URLSearchParams(hashQuery).forEach((value, key) => {
          params[key] = value;
        });
      }
      return params;
    };

    const hashParams = getParamsFromHashQuery(window.location.hash);
    partnerName = hashParams['s'];
    console.log("main.tsx - Raw partnerName from URL hash param 's':", partnerName);
    const searchParams = new URLSearchParams(window.location.search || "");
    const getCookie = (name: string) => {
      if (typeof document === "undefined") return null;
      const match = document.cookie.split("; ").find(row => row.startsWith(`${name}=`));
      return match ? decodeURIComponent(match.split("=").slice(1).join("=")) : null;
    };
    const setCookie = (name: string, value: string, maxAgeSeconds?: number) => {
      if (typeof document === "undefined") return;
      const maxAgePart = typeof maxAgeSeconds === "number" ? `; max-age=${maxAgeSeconds}` : "";
      document.cookie = `${name}=${encodeURIComponent(value)}; path=/${maxAgePart}`;
    };
    const setInitialAgentFromSources = async () => {
      let determinedProfile: { agentId: string | null; agentName: string | null; agentToken: string | null; agentEmails: string[]; isDirectAgent: boolean } | null = null;
      const cookieAgentToken = getCookie("AGENT_TOKEN");
      const searchAgentParam = searchParams.get("agent");

      const directAgentParam = searchAgentParam || hashParams['agent'];
      const normalizedDirectAgentId = normalizeAgentId(directAgentParam ?? null);

      if (normalizedDirectAgentId) {
        determinedProfile = {
          agentId: normalizedDirectAgentId,
          agentName: null,
          agentToken: null,
          agentEmails: [],
          isDirectAgent: true,
        };
        console.log("main.tsx - Direct agent provided via URL:", determinedProfile);
      } else if (hashParams['agentToken']) {
        try {
          const broker = await fetchBrokerByToken(hashParams['agentToken']);
          determinedProfile = {
            agentId: broker.uid || null,
            agentName: broker.name || null,
            agentToken: broker.token || null,
            agentEmails: Array.isArray(broker.emailAddresses) ? broker.emailAddresses : [],
            isDirectAgent: false,
          };
          if (broker.token) {
            setCookie("AGENT_TOKEN", broker.token);
          }
          console.log("main.tsx - Broker fetched via token:", determinedProfile);
        } catch (e) {
          console.error("main.tsx - Failed to fetch broker by token:", e);
        }
      } else if (cookieAgentToken) {
        try {
          const broker = await fetchBrokerByToken(cookieAgentToken);
          determinedProfile = {
            agentId: broker.uid || null,
            agentName: broker.name || null,
            agentToken: broker.token || null,
            agentEmails: Array.isArray(broker.emailAddresses) ? broker.emailAddresses : [],
            isDirectAgent: false,
          };
          console.log("main.tsx - Broker fetched via cookie token:", determinedProfile);
        } catch (e) {
          console.error("main.tsx - Failed to fetch broker by cookie token:", e);
        }
      } else if (hashParams['agentID']) {
        determinedProfile = {
          agentId: hashParams['agentID'],
          agentName: null,
          agentToken: null,
          agentEmails: [],
          isDirectAgent: false,
        };
      }

      setInitialAgentProfile(determinedProfile);
      console.log("main.tsx - Determined initial agent profile:", determinedProfile);
    };

    const loadWlpTranslations = async () => {
      await setInitialAgentFromSources();

      const supported = i18n.options.supportedLngs;
      const widgetLocale = typeof window !== "undefined" ? window.__simpego_widget__?.locale : null;
      let preferredLocale: string | null = null;

      if (widgetLocale && supported?.includes(widgetLocale)) {
        preferredLocale = widgetLocale;
      }

      if (!preferredLocale) {
        const hashPathSegments = window.location.hash.split('?')[0].split('/').filter(Boolean);
        if (hashPathSegments.length > 0 && supported?.includes(hashPathSegments[0])) {
          preferredLocale = hashPathSegments[0];
        }
      }

      if (partnerName) {
        try {
          const translationPath = `/${partnerName}-translations.json`;
          console.log("main.tsx - Attempting to fetch translations from:", translationPath);
          const response = await fetch(translationPath);
          if (response.ok) {
            const wlpTranslations = await response.json();
            for (const langKey in wlpTranslations) {
              if (Object.prototype.hasOwnProperty.call(wlpTranslations, langKey)) {
                i18n.addResourceBundle(langKey, 'translation', wlpTranslations[langKey], true, true);
              }
            }
            console.log(`WLP translations for '${partnerName}' loaded and applied.`);
          } else {
            console.warn(`WLP translations file for '${partnerName}' not found or failed to load. Using base translations.`);
          }
        } catch (error) {
          console.error(`Error loading WLP translations for '${partnerName}':`, error);
        }
      }

      if (preferredLocale && i18n.language !== preferredLocale) {
        await i18n.changeLanguage(preferredLocale);
      }
      localizeFlatpickr(i18n.language);
      i18n.on("languageChanged", localizeFlatpickr);
      setIsI18nReady(true);

      const currentHash = window.location.hash;
      const [hashPath, hashQuery] = currentHash.split('?');
      const hashParamsObj = new URLSearchParams(hashQuery);

      let updatedHash = false;
      if (hashParamsObj.has('s')) {
        hashParamsObj.delete('s');
        updatedHash = true;
      }
      if (hashParamsObj.has('agentID')) {
        hashParamsObj.delete('agentID');
        updatedHash = true;
      }
      if (hashParamsObj.has('agentToken')) {
        hashParamsObj.delete('agentToken');
        updatedHash = true;
      }
      if (hashParamsObj.has('agent')) {
        hashParamsObj.delete('agent');
        updatedHash = true;
      }

      if (updatedHash) {
        const newHash = `${hashPath}${hashParamsObj.toString() ? `?${hashParamsObj.toString()}` : ''}`;
        window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search}${newHash}`);
        console.log("main.tsx - Removed 's', 'agentID', 'agentToken', and 'agent' parameters from URL hash.");
      }
    };

    loadWlpTranslations();

    return () => {
      i18n.off("languageChanged", localizeFlatpickr);
    };
  }, []);

  if (!isI18nReady) {
    return <div>Loading translations...</div>;
  }

  return (
    <Sentry.ErrorBoundary>
    <I18nextProvider i18n={i18n}>
      <AgentIdProvider initialAgentProfile={initialAgentProfile}>
        <ErrorDialogProvider>
          <App />
        </ErrorDialogProvider>
      </AgentIdProvider>
    </I18nextProvider>
    </Sentry.ErrorBoundary>
  );
};

createRoot(rootElement).render(<Root />);
